import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiUpload, ApiError } from '../api/client';
import { Avatar } from '../components/Avatar';
import { PasswordField } from '../components/PasswordField';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { useAuth, type AuthUser, type UserRole } from '../auth/AuthContext';

const ROLE_LABEL: Record<UserRole, string> = {
  client: 'Vous commandez des courses',
  deliverer: 'Vous livrez les courses',
  business: 'Vous envoyez des courses pour votre activité',
  admin: 'Vous administrez la plateforme'
};

interface ProfileResponse {
  success: true;
  data: AuthUser;
}

export const ProfilePage = () => {
  const { user, replaceUser } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<ProfileResponse>('/api/v1/auth/profile');
        if (cancelled) return;
        replaceUser({ ...user!, ...res.data });
        setFirstName(res.data.firstName ?? '');
        setLastName(res.data.lastName ?? '');
        setPhoneNumber(res.data.phoneNumber ?? '');
      } catch {
        // Le profil déjà en mémoire reste affiché.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Chargement initial seulement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveInfo = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfoMessage(null);
    setSavingInfo(true);
    try {
      const res = await apiFetch<ProfileResponse>('/api/v1/auth/profile', {
        method: 'PUT',
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: phoneNumber.trim()
        }
      });
      replaceUser({ ...user!, ...res.data });
      setInfoMessage('Vos informations sont enregistrées.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Enregistrement impossible');
    } finally {
      setSavingInfo(false);
    }
  };

  const onAvatarChange = async (file: File | undefined) => {
    if (!file || !user) return;
    setError(null);
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append('avatar', file);
      const res = await apiUpload<ProfileResponse>('/api/v1/auth/profile/avatar', { form });
      replaceUser({ ...user, ...res.data });
      setInfoMessage('Photo de profil mise à jour.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload impossible');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const savePassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPasswordMessage(null);
    if (newPassword.length < 6) {
      setError('Le nouveau mot de passe fait au moins 6 caractères.');
      return;
    }
    setSavingPassword(true);
    try {
      await apiFetch('/api/v1/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword }
      });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordMessage('Le mot de passe est changé. Utilisez-le à la prochaine connexion.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Changement impossible');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) return null;

  return (
    <div className="animate-fade-in space-y-6">
      <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white p-6 shadow-soft">
        <p className="text-sm font-medium text-brand-700">Bonjour {user.firstName}</p>
        <PageHeader
          title="Mon profil"
          description="Corrigez votre nom, votre téléphone et votre mot de passe. L’adresse e-mail sert à vous connecter."
        />
        <p className="text-sm text-slate-600">{ROLE_LABEL[user.role]}</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <div className="card space-y-4 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Photo de profil</h2>
        <div className="flex flex-wrap items-center gap-4">
          <Avatar
            size="lg"
            profilePictureUrl={user.profilePictureUrl}
            firstName={user.firstName}
            lastName={user.lastName}
          />
          <div className="space-y-2">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              id="avatar-upload"
              onChange={(e) => onAvatarChange(e.target.files?.[0])}
            />
            <label htmlFor="avatar-upload" className={`btn-ghost cursor-pointer ${uploadingAvatar ? 'pointer-events-none opacity-60' : ''}`}>
              {uploadingAvatar ? (
                <Spinner className="h-4 w-4 border-2 border-brand-500 border-t-transparent" />
              ) : null}
              Changer la photo
            </label>
            <p className="text-xs text-slate-500">JPEG, PNG ou WebP — visible sur vos courses.</p>
          </div>
        </div>
      </div>

      <form onSubmit={saveInfo} className="card space-y-4 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Vos informations</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Prénom</span>
            <input className="input mt-1.5" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </label>
          <label>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Nom</span>
            <input className="input mt-1.5" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </label>
        </div>
        <label>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Téléphone</span>
          <input className="input mt-1.5" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
        </label>
        <label>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">E-mail</span>
          <input className="input mt-1.5 bg-slate-50 text-slate-500" value={user.email} readOnly />
        </label>
        {infoMessage && <p className="text-sm text-emerald-700">{infoMessage}</p>}
        <button type="submit" className="btn-primary" disabled={savingInfo}>
          {savingInfo ? <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" /> : null}
          Enregistrer
        </button>
      </form>

      <form onSubmit={savePassword} className="card space-y-4 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Mot de passe</h2>
        <p className="text-sm text-slate-500">Saisissez l’ancien, puis le nouveau. Affichez-les si vous voulez vérifier la frappe.</p>
        <PasswordField label="Mot de passe actuel" autoComplete="current-password" value={currentPassword} onChange={setCurrentPassword} />
        <PasswordField label="Nouveau mot de passe" autoComplete="new-password" value={newPassword} onChange={setNewPassword} />
        {passwordMessage && <p className="text-sm text-emerald-700">{passwordMessage}</p>}
        <button type="submit" className="btn-primary" disabled={savingPassword || !currentPassword || !newPassword}>
          {savingPassword ? <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" /> : null}
          Changer le mot de passe
        </button>
      </form>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-slate-900">Documents</h2>
        <p className="mt-1 text-sm text-slate-500">
          Une pièce d’identité aide à confirmer qui vous êtes. Ce n’est pas obligatoire pour voir vos courses.
        </p>
        <Link to="/kyc" className="mt-4 inline-flex btn-ghost">
          Envoyer un document
        </Link>
      </div>
    </div>
  );
};
